import { motion } from 'framer-motion';

const ChartBackground = () => {
  // Generate multiple chart lines with different speeds
  const lines = Array.from({ length: 6 }, (_, i) => ({
    id: i,
    speed: 20 + i * 5,
    opacity: 0.05 + i * 0.02,
    color: ['#dc2626', '#3b82f6', '#22c55e', '#a855f7', '#f59e0b', '#ec4899'][i % 6],
    yPosition: 15 + i * 12,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Animated Chart Lines - Seamless Loop */}
      {lines.map((line) => (
        <div
          key={line.id}
          className="absolute w-[200%] h-32"
          style={{
            bottom: `${line.yPosition}%`,
            opacity: line.opacity,
          }}
        >
          <motion.div
            className="flex"
            animate={{
              x: ['0%', '-50%'],
            }}
            transition={{
              x: {
                repeat: Infinity,
                repeatType: 'loop',
                duration: line.speed,
                ease: 'linear',
              },
            }}
          >
            {/* First copy */}
            <svg
              viewBox="0 0 1200 100"
              preserveAspectRatio="none"
              className="w-[50%] h-full"
            >
              <path
                d="M0,80 Q150,70 300,50 T600,40 T900,25 T1200,10"
                fill="none"
                stroke={line.color}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M0,90 Q200,60 400,70 T800,30 T1200,20"
                fill="none"
                stroke={line.color}
                strokeWidth="1.5"
                strokeOpacity="0.5"
                strokeLinecap="round"
              />
            </svg>
            {/* Second copy (identical for seamless loop) */}
            <svg
              viewBox="0 0 1200 100"
              preserveAspectRatio="none"
              className="w-[50%] h-full"
            >
              <path
                d="M0,80 Q150,70 300,50 T600,40 T900,25 T1200,10"
                fill="none"
                stroke={line.color}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M0,90 Q200,60 400,70 T800,30 T1200,20"
                fill="none"
                stroke={line.color}
                strokeWidth="1.5"
                strokeOpacity="0.5"
                strokeLinecap="round"
              />
            </svg>
          </motion.div>
        </div>
      ))}

      {/* Seamless Candlestick Pattern */}
      <div className="absolute bottom-0 left-0 right-0 h-48 opacity-[0.04]">
        <motion.div
          className="flex w-[200%]"
          animate={{
            x: ['0%', '-50%'],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: 'loop',
              duration: 30,
              ease: 'linear',
            },
          }}
        >
          {/* First set of candles */}
          <div className="flex w-1/2">
            {Array.from({ length: 20 }).map((_, i) => {
              const isGreen = i % 2 === 0;
              const height = 30 + (i % 5) * 15;
              return (
                <div
                  key={`first-${i}`}
                  className="flex-shrink-0 w-12 mx-4 flex flex-col items-center"
                >
                  <div
                    className="w-3 rounded-sm"
                    style={{
                      height: `${height}px`,
                      backgroundColor: isGreen ? '#22c55e' : '#dc2626',
                    }}
                  />
                  <div
                    className="w-px h-8 mt-1"
                    style={{
                      backgroundColor: isGreen ? '#22c55e' : '#dc2626',
                    }}
                  />
                </div>
              );
            })}
          </div>
          {/* Second set (identical for seamless loop) */}
          <div className="flex w-1/2">
            {Array.from({ length: 20 }).map((_, i) => {
              const isGreen = i % 2 === 0;
              const height = 30 + (i % 5) * 15;
              return (
                <div
                  key={`second-${i}`}
                  className="flex-shrink-0 w-12 mx-4 flex flex-col items-center"
                >
                  <div
                    className="w-3 rounded-sm"
                    style={{
                      height: `${height}px`,
                      backgroundColor: isGreen ? '#22c55e' : '#dc2626',
                    }}
                  />
                  <div
                    className="w-px h-8 mt-1"
                    style={{
                      backgroundColor: isGreen ? '#22c55e' : '#dc2626',
                    }}
                  />
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Rising Trend Lines - Seamless */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06]" preserveAspectRatio="none">
        <defs>
          <pattern id="trendLines" x="0" y="0" width="100%" height="100%" patternUnits="userSpaceOnUse">
            <line
              x1="0"
              y1="100%"
              x2="100%"
              y2="0%"
              stroke="#dc2626"
              strokeWidth="0.5"
              strokeDasharray="4,8"
            />
            <line
              x1="0"
              y1="85%"
              x2="90%"
              y2="15%"
              stroke="#3b82f6"
              strokeWidth="0.5"
              strokeDasharray="4,8"
            />
            <line
              x1="10%"
              y1="100%"
              x2="100%"
              y2="20%"
              stroke="#22c55e"
              strokeWidth="0.5"
              strokeDasharray="4,8"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#trendLines)" />
      </svg>

      {/* Grid Overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(to right, #dc2626 1px, transparent 1px),
              linear-gradient(to bottom, #dc2626 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      {/* Bottom Pulse Line */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-crypto-red to-transparent"
        animate={{
          opacity: [0, 0.6, 0],
          scaleX: [0, 1, 0],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          repeatDelay: 3,
          ease: 'easeInOut',
        }}
      />
    </div>
  );
};

export default ChartBackground;
